// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, Skeleton, Alert } from '@/components/ui'
import { useProperty } from '@/hooks/useProperties'
import {
  useCommonAreaFee,
  useCreateCommonAreaFee,
  useUpdateCommonAreaFee,
  useCommonAreaFeePreview,
} from '@/hooks/useCommonAreaFees'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'
import type { CommonAreaFee } from '@/types'

interface FeeFormData {
  name: string
  fee_type: CommonAreaFee['fee_type']
  calculation_method: CommonAreaFee['calculation_method']
  rate: number
  vat_rate: number
  effective_from: string
  effective_until: string
  description: string
}

export function CommonAreaFeeFormPage() {
  const { t } = useTranslation('common_area_fees')
  const { t: tc } = useTranslation('common')
  const { t: tp } = useTranslation('properties')
  const translateError = useApiErrorTranslator('common_area_fees')
  const addToast = useNotificationStore((s) => s.addToast)

  const { propertyId, id } = useParams<{ propertyId: string; id: string }>()
  const navigate = useNavigate()

  const propertyIdNum = Number(propertyId)
  const feeId = id ? Number(id) : undefined
  const isEditing = !!feeId

  const { data: property, isLoading: isLoadingProperty } = useProperty(propertyIdNum)
  const { data: existingFee, isLoading: isLoadingFee } = useCommonAreaFee(
    propertyIdNum,
    feeId ?? 0
  )

  const createFee = useCreateCommonAreaFee()
  const updateFee = useUpdateCommonAreaFee()

  const [showPreview, setShowPreview] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FeeFormData>({
    defaultValues: {
      name: '',
      fee_type: 'maintenance_fund',
      calculation_method: 'per_unit',
      rate: 0,
      vat_rate: 7,
      effective_from: '',
      effective_until: '',
      description: '',
    },
  })

  const watchedValues = watch()

  // Preview query — only when the panel is open and we have the required values
  const previewEnabled =
    showPreview &&
    !!watchedValues.calculation_method &&
    watchedValues.rate > 0

  const { data: preview, isLoading: isLoadingPreview } = useCommonAreaFeePreview(
    propertyIdNum,
    {
      fee_type: watchedValues.fee_type,
      calculation_method: watchedValues.calculation_method,
      rate: watchedValues.rate,
      vat_rate: watchedValues.vat_rate,
    },
    previewEnabled
  )

  useEffect(() => {
    if (existingFee) {
      reset({
        name: existingFee.name,
        fee_type: existingFee.fee_type,
        calculation_method: existingFee.calculation_method,
        rate: existingFee.rate,
        vat_rate: existingFee.vat_rate,
        effective_from: existingFee.effective_from,
        effective_until: existingFee.effective_until ?? '',
        description: existingFee.description ?? '',
      })
    }
  }, [existingFee, reset])

  const onSubmit = async (data: FeeFormData) => {
    const payload = {
      propertyId: propertyIdNum,
      name: data.name,
      fee_type: data.fee_type,
      calculation_method: data.calculation_method,
      rate: Number(data.rate),
      vat_rate: Number(data.vat_rate),
      effective_from: data.effective_from,
      effective_until: data.effective_until || null,
      description: data.description || null,
    }

    try {
      if (isEditing && feeId) {
        await updateFee.mutateAsync({ id: feeId, ...payload })
      } else {
        await createFee.mutateAsync(payload)
      }
      navigate(`/properties/${propertyId}`)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const isLoading = isLoadingProperty || (isEditing && isLoadingFee)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <Skeleton className="h-64" />
        </Card>
      </div>
    )
  }

  const feeTypeOptions = [
    { value: 'maintenance_fund', label: t('fee_types.maintenance_fund') },
    { value: 'sinking_fund', label: t('fee_types.sinking_fund') },
    { value: 'insurance', label: t('fee_types.insurance') },
    { value: 'other', label: t('fee_types.other') },
  ]

  const calculationMethodOptions = [
    { value: 'per_unit', label: t('calculation_methods.per_unit') },
    { value: 'per_sqm', label: t('calculation_methods.per_sqm') },
    { value: 'per_share_ratio', label: t('calculation_methods.per_share_ratio') },
    { value: 'fixed', label: t('calculation_methods.fixed_amount') },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: tp('title'), href: '/properties' },
          { label: property?.name ?? '...', href: `/properties/${propertyId}` },
          { label: isEditing ? t('edit_fee') : t('new_fee') },
        ]}
      />

      <PageHeader
        title={isEditing ? t('edit_fee') : t('new_fee')}
        description={t('description')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card title={t('title')}>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label={t('labels.name')}
              placeholder={t('placeholders.name')}
              error={errors.name?.message}
              {...register('name', { required: t('validation.name_required') })}
            />
            <Select
              label={t('labels.fee_type')}
              options={feeTypeOptions}
              error={errors.fee_type?.message}
              {...register('fee_type', { required: t('validation.fee_type_required') })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 mt-4">
            <Select
              label={t('labels.calculation_method')}
              options={calculationMethodOptions}
              error={errors.calculation_method?.message}
              {...register('calculation_method', {
                required: t('validation.calculation_method_required'),
              })}
            />
            <Input
              label={t('labels.rate')}
              type="number"
              step="0.01"
              placeholder={t('placeholders.rate')}
              error={errors.rate?.message}
              {...register('rate', {
                required: t('validation.rate_required'),
                valueAsNumber: true,
                min: { value: 0.01, message: t('validation.rate_positive') },
              })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 mt-4">
            <Input
              label={t('labels.vat_rate')}
              type="number"
              step="0.01"
              placeholder={t('placeholders.vat_rate')}
              error={errors.vat_rate?.message}
              {...register('vat_rate', {
                valueAsNumber: true,
                min: { value: 0, message: t('validation.vat_rate_range') },
                max: { value: 100, message: t('validation.vat_rate_range') },
              })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 mt-4">
            <Input
              label={t('labels.effective_from')}
              type="date"
              error={errors.effective_from?.message}
              {...register('effective_from', {
                required: t('validation.effective_from_required'),
              })}
            />
            <Input
              label={t('labels.effective_until')}
              type="date"
              {...register('effective_until')}
            />
          </div>

          {/* description field removed — no DB column */}
        </Card>

        {/* Preview section */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold">{t('preview.title')}</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? t('actions.hide_preview') : t('actions.preview')}
            </Button>
          </div>

          {showPreview && (
            <>
              {!previewEnabled && (
                <p className="text-sm text-base-content/60">{t('preview.description')}</p>
              )}

              {previewEnabled && isLoadingPreview && (
                <div className="space-y-2">
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                </div>
              )}

              {previewEnabled && !isLoadingPreview && preview && (
                <>
                  {/* Warnings */}
                  {preview.warnings && preview.warnings.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {preview.warnings.map((warning, i) => (
                        <Alert key={i} variant="warning">
                          {warning}
                        </Alert>
                      ))}
                    </div>
                  )}

                  {preview.units.length === 0 ? (
                    <p className="text-sm text-base-content/60 py-4 text-center">
                      {t('preview.empty')}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table table-sm w-full">
                        <thead>
                          <tr>
                            <th>{t('labels.unit_number')}</th>
                            <th className="text-right">{t('labels.area_sqm')}</th>
                            <th className="text-right">{t('labels.share_ratio')}</th>
                            <th className="text-right">{t('labels.calculated_amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.units.map((unit) => (
                            <tr key={unit.unit_id}>
                              <td className="font-medium">{unit.unit_name || unit.unit_number}</td>
                              <td className="text-right text-base-content/70">
                                {unit.area_sqm != null ? unit.area_sqm.toFixed(2) : '—'}
                              </td>
                              <td className="text-right text-base-content/70">
                                {unit.share_ratio != null ? unit.share_ratio.toFixed(4) : '—'}
                              </td>
                              <td className="text-right font-medium">
                                {formatBaht(unit.amount, { decimals: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-semibold border-t-2 border-base-200">
                            <td colSpan={3}>{t('preview.total')}</td>
                            <td className="text-right">
                              {formatBaht(preview.total_amount, { decimals: 2 })}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Card>

        <FormActions>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            {tc('actions.cancel')}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEditing ? tc('actions.save_changes') : t('actions.add_fee')}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
